import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';

const serviceLinks = [
  { label: 'Residential Settlements', path: '/services/residential-settlements' },
  { label: 'Title Insurance', path: '/services/title-insurance' },
  { label: 'Title Searches', path: '/services/title-searches' },
  { label: 'Commercial Closings', path: '/services/commercial-closings' },
  { label: '1031 Exchanges', path: '/services/1031-exchanges' },
  { label: 'Refinancing', path: '/services/refinancing' },
  { label: 'Deed Preparation', path: '/services/deed-preparation' },
  { label: 'Lien Searches', path: '/services/lien-searches' },
];

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setServicesOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setServicesOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isActive = (path: string) =>
    path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);

  const isServicesActive = location.pathname.startsWith('/services');

  return (
    <>
      <style>{`
        @media (max-width: 767px) {
          .nav-desktop-links { display: none !important; }
          .nav-desktop-phone { display: none !important; }
          .nav-mobile-hamburger { display: flex !important; }
          .nav-mobile-callbar { display: block !important; }
          .nav-header { top: 38px !important; }
        }
        @media (min-width: 768px) {
          .nav-desktop-links { display: flex !important; }
          .nav-desktop-phone { display: flex !important; }
          .nav-mobile-hamburger { display: none !important; }
          .nav-mobile-callbar { display: none !important; }
          .nav-header { top: 0 !important; }
          .nav-mobile-overlay { display: none !important; }
        }
        .services-dropdown {
          opacity: 0;
          pointer-events: none;
          transform: translateY(-6px);
          transition: opacity 0.18s ease, transform 0.18s ease;
        }
        .services-dropdown.open {
          opacity: 1;
          pointer-events: auto;
          transform: translateY(0);
        }
      `}</style>

      {/* Mobile sticky call bar */}
      <div className="nav-mobile-callbar fixed top-0 left-0 right-0 z-[60] bg-[#c9a84c]" style={{ display: 'none' }}>
        <a
          href="tel:2154990796"
          className="flex items-center justify-center gap-2 py-2.5 text-[#0f0f0f] font-bold text-xs tracking-[0.15em] uppercase whitespace-nowrap cursor-pointer"
        >
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-phone-fill text-sm"></i>
          </div>
          Call Us: 215-499-0796
        </a>
      </div>

      {/* Main header */}
      <header
        className={`nav-header fixed left-0 right-0 z-50 transition-all duration-500 ${
          scrolled ? 'bg-[#0f0f0f] border-b border-white/5' : 'bg-transparent'
        }`}
      >
        <div className="px-6 md:px-12 flex items-center justify-between h-[72px]">

          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 cursor-pointer shrink-0">
            <img
              src="https://storage.readdy-site.link/project_files/24cbc93a-97c5-4a4c-8795-e638165a0b41/150f0a6f-f0eb-4598-a9c6-5fbc1a938f2f_black-dog-settlement-services-white.png?v=f6ded5401678ec51453df7ea2bb5dc1a"
              alt="Black Dog Settlement Services"
              className="h-10 w-auto object-contain"
            />
          </Link>

          {/* Desktop Nav — centered */}
          <nav className="nav-desktop-links absolute left-1/2 -translate-x-1/2 items-center gap-10">
            <Link
              to="/"
              className={`text-xs font-semibold tracking-[0.18em] uppercase transition-colors duration-200 whitespace-nowrap cursor-pointer ${
                isActive('/') ? 'text-[#c9a84c]' : 'text-white/80 hover:text-white'
              }`}
            >
              Home
            </Link>

            <Link
              to="/about"
              className={`text-xs font-semibold tracking-[0.18em] uppercase transition-colors duration-200 whitespace-nowrap cursor-pointer ${
                isActive('/about') ? 'text-[#c9a84c]' : 'text-white/80 hover:text-white'
              }`}
            >
              About
            </Link>

            {/* Services dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setServicesOpen(!servicesOpen)}
                className={`flex items-center gap-1 text-xs font-semibold tracking-[0.18em] uppercase transition-colors duration-200 whitespace-nowrap cursor-pointer ${
                  isServicesActive ? 'text-[#c9a84c]' : 'text-white/80 hover:text-white'
                }`}
              >
                Services
                <div className="w-3 h-3 flex items-center justify-center">
                  <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${servicesOpen ? 'rotate-180' : ''}`}></i>
                </div>
              </button>

              {/* Dropdown panel */}
              <div className={`services-dropdown ${servicesOpen ? 'open' : ''} absolute top-full left-1/2 -translate-x-1/2 mt-4 w-64 bg-[#0f0f0f] border border-white/10`}>
                <div className="py-2">
                  <Link
                    to="/services"
                    className="block px-5 py-2.5 text-xs font-bold tracking-[0.15em] uppercase text-[#c9a84c] border-b border-white/10 hover:bg-white/5 transition-colors cursor-pointer"
                  >
                    All Services
                  </Link>
                  {serviceLinks.map((s) => (
                    <Link
                      key={s.path}
                      to={s.path}
                      className={`block px-5 py-2.5 text-xs tracking-wide transition-colors cursor-pointer ${
                        location.pathname === s.path
                          ? 'text-[#c9a84c] bg-white/5'
                          : 'text-white/70 hover:text-white hover:bg-white/5'
                      }`}
                    >
                      {s.label}
                    </Link>
                  ))}
                </div>
              </div>
            </div>

            <Link
              to="/contact"
              className={`text-xs font-semibold tracking-[0.18em] uppercase transition-colors duration-200 whitespace-nowrap cursor-pointer ${
                isActive('/contact') ? 'text-[#c9a84c]' : 'text-white/80 hover:text-white'
              }`}
            >
              Contact
            </Link>
          </nav>

          {/* Desktop phone */}
          <a
            href="tel:2154990796"
            className="nav-desktop-phone items-center gap-2 text-xs font-bold tracking-wide text-white/80 hover:text-[#c9a84c] transition-colors whitespace-nowrap cursor-pointer shrink-0"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-phone-line text-[#c9a84c]"></i>
            </div>
            215-499-0796
          </a>

          {/* Mobile hamburger only */}
          <button
            className="nav-mobile-hamburger w-10 h-10 items-center justify-center cursor-pointer text-white"
            style={{ display: 'none' }}
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
          >
            <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-3-line'}`}></i>
          </button>
        </div>
      </header>

      {/* Mobile full-screen menu */}
      <div
        className={`nav-mobile-overlay fixed inset-0 z-40 bg-[#0f0f0f] flex flex-col justify-center px-10 transition-all duration-500 overflow-y-auto ${
          mobileOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
      >
        <div className="space-y-6 mt-10">
          <Link to="/" className="block text-4xl font-bold text-white hover:text-[#c9a84c] transition-colors cursor-pointer">
            Home
          </Link>
          <Link to="/about" className="block text-4xl font-bold text-white hover:text-[#c9a84c] transition-colors cursor-pointer">
            About
          </Link>

          {/* Mobile services accordion */}
          <div>
            <button
              onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
              className="flex items-center gap-3 text-4xl font-bold text-white hover:text-[#c9a84c] transition-colors cursor-pointer w-full text-left"
            >
              Services
              <div className="w-8 h-8 flex items-center justify-center">
                <i className={`ri-arrow-down-s-line text-2xl transition-transform duration-200 ${mobileServicesOpen ? 'rotate-180' : ''}`}></i>
              </div>
            </button>
            {mobileServicesOpen && (
              <div className="mt-3 ml-4 space-y-3 border-l border-white/10 pl-5">
                <Link to="/services" className="block text-sm font-bold tracking-widest uppercase text-[#c9a84c] cursor-pointer">
                  All Services
                </Link>
                {serviceLinks.map((s) => (
                  <Link
                    key={s.path}
                    to={s.path}
                    className="block text-base text-white/70 hover:text-white transition-colors cursor-pointer"
                  >
                    {s.label}
                  </Link>
                ))}
              </div>
            )}
          </div>

          <Link to="/contact" className="block text-4xl font-bold text-white hover:text-[#c9a84c] transition-colors cursor-pointer">
            Contact
          </Link>
        </div>
        <div className="mt-16 pt-10 border-t border-white/10">
          <a href="tel:2154990796" className="text-[#c9a84c] font-bold text-lg cursor-pointer">215-499-0796</a>
          <p className="text-white/40 text-sm mt-1">info@blackdogsettlement.com</p>
        </div>
      </div>
    </>
  );
};

export default Navbar;
